import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  Card,
  CardBody,
  CardHeader,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  SimpleGrid,
  Code,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
  TableContainer,
  useClipboard,
  Collapse,
  useDisclosure,
  Divider,
  Input,
  Select
} from '@chakra-ui/react';
import {
  FiBook,
  FiCode,
  FiUser,
  FiSettings,
  FiDatabase,
  FiGlobe,
  FiFileText,
  FiTag,
  FiImage,
  FiNavigation,
  FiLayout,
  FiCalendar,
  FiTrendingUp,
  FiCheck,
  FiCopy,
  FiChevronDown,
  FiChevronRight,
  FiPlay,
  FiEye,
  FiKey,
  FiServer,
  FiUsers,
  FiCreditCard,
  FiFolder,
  FiGrid,
  FiMap,
  FiShield
} from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';

// Create motion components
const MotionCard = motion(Card);
const MotionBox = motion(Box);

const Documentation = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [apiResponse, setApiResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { hasCopied, onCopy } = useClipboard('');
  
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const codeBgColor = useColorModeValue('gray.100', 'gray.700');
  const codeColor = useColorModeValue('gray.800', 'gray.100');
  const sidebarBg = useColorModeValue('gray.50', 'gray.800');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');

  const copyToClipboard = (code) => {
    onCopy(code);
  };

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  // Comprehensive API endpoints for all entities
  const managementApiEndpoints = [
    {
      category: 'User Management',
      icon: FiUser,
      color: 'blue',
      description: 'Manage users, authentication, roles and permissions',
      endpoints: [
        {
          method: 'POST',
          path: '/api/users/register',
          description: 'Register new user account',
          summary: 'Create a new user account with email and password',
          parameters: [],
          requestBody: {
            email: 'user@example.com',
            password: 'securePassword123',
            name: 'John Doe',
            role: 'user'
          },
          responses: {
            201: {
              description: 'User created successfully',
              example: {
                success: true,
                message: 'User registered successfully',
                user: {
                  id: 1,
                  email: 'user@example.com',
                  name: 'John Doe',
                  created_at: '2025-01-07T10:30:00Z'
                }
              }
            }
          }
        },
        {
          method: 'POST',
          path: '/api/users/login',
          description: 'Authenticate user login',
          summary: 'Authenticate user and return JWT token',
          parameters: [],
          requestBody: {
            email: 'user@example.com',
            password: 'securePassword123'
          },
          responses: {
            200: {
              description: 'Login successful',
              example: {
                success: true,
                token: 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9...',
                user: {
                  id: 1,
                  email: 'user@example.com',
                  name: 'John Doe',
                  tenant_ids: [1]
                }
              }
            }
          }
        },
        {
          method: 'GET',
          path: '/api/users/{tenant_id}',
          description: 'Get all users for tenant',
          summary: 'Retrieve all users belonging to a specific tenant',
          parameters: [
            { name: 'tenant_id', type: 'path', required: true, description: 'Tenant ID' },
            { name: 'page', type: 'query', required: false, description: 'Page number (default: 1)' },
            { name: 'limit', type: 'query', required: false, description: 'Items per page (default: 20)' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'Users retrieved successfully',
              example: {
                success: true,
                data: [
                  {
                    id: 1,
                    email: 'user@example.com',
                    name: 'John Doe',
                    role: 'admin',
                    created_at: '2025-01-07T10:30:00Z'
                  }
                ]
              }
            }
          }
        }
      ]
    },
    {
      category: 'Tenant Management',
      icon: FiUsers,
      color: 'purple',
      description: 'Manage organizations and tenant configurations',
      endpoints: [
        {
          method: 'GET',
          path: '/api/tenants',
          description: 'Get all tenants',
          summary: 'Retrieve all available tenants',
          parameters: [],
          requestBody: null,
          responses: {
            200: {
              description: 'Tenants retrieved successfully',
              example: {
                success: true,
                data: [
                  {
                    id: 1,
                    name: 'Acme Corporation',
                    created_at: '2025-01-07T10:30:00Z'
                  }
                ]
              }
            }
          }
        },
        {
          method: 'POST',
          path: '/api/tenants',
          description: 'Create new tenant',
          summary: 'Create a new tenant organization',
          parameters: [],
          requestBody: {
            name: 'New Organization'
          },
          responses: {
            201: {
              description: 'Tenant created successfully',
              example: {
                success: true,
                data: {
                  id: 2,
                  name: 'New Organization',
                  created_at: '2025-01-07T11:30:00Z'
                }
              }
            }
          }
        }
      ]
    },
    {
      category: 'Site Management',
      icon: FiGlobe,
      color: 'green',
      description: 'Manage websites and their configurations',
      endpoints: [
        {
          method: 'GET',
          path: '/api/sites/{tenant_id}',
          description: 'Get all sites for tenant',
          summary: 'Retrieve all sites belonging to a tenant',
          parameters: [
            { name: 'tenant_id', type: 'path', required: true, description: 'Tenant ID' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'Sites retrieved successfully',
              example: {
                success: true,
                data: [
                  {
                    id: 1,
                    name: 'Main Website',
                    domain: 'example.com',
                    site_status: 'published',
                    default_locale_id: 1,
                    created_at: '2025-01-07T10:30:00Z'
                  }
                ]
              }
            }
          }
        },
        {
          method: 'POST',
          path: '/api/sites',
          description: 'Create new site',
          summary: 'Create a new website for a tenant',
          parameters: [],
          requestBody: {
            tenant_id: 1,
            name: 'New Website',
            domain: 'newsite.com',
            site_status: 'draft',
            default_locale_id: 1,
            description: 'A new website'
          },
          responses: {
            201: {
              description: 'Site created successfully',
              example: {
                success: true,
                data: {
                  id: 2,
                  message: 'Site created successfully'
                }
              }
            }
          }
        }
      ]
    },
    {
      category: 'Content Models',
      icon: FiDatabase,
      color: 'orange',
      description: 'Define and manage content structures',
      endpoints: [
        {
          method: 'GET',
          path: '/api/content-models/{tenant_id}',
          description: 'Get all content models',
          summary: 'Retrieve all content models for a tenant',
          parameters: [
            { name: 'tenant_id', type: 'path', required: true, description: 'Tenant ID' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'Content models retrieved successfully',
              example: {
                success: true,
                data: [
                  {
                    id: 1,
                    name: 'Blog Post',
                    fields: [
                      {
                        name: 'title',
                        type: 'text',
                        required: true
                      },
                      {
                        name: 'content',
                        type: 'richtext',
                        required: true
                      }
                    ],
                    created_at: '2025-01-07T10:30:00Z'
                  }
                ]
              }
            }
          }
        },
        {
          method: 'POST',
          path: '/api/content-models',
          description: 'Create content model',
          summary: 'Create a new content model with custom fields',
          parameters: [],
          requestBody: {
            tenant_id: 1,
            name: 'Product',
            description: 'Product content model',
            fields: [
              {
                name: 'name',
                type: 'text',
                required: true,
                label: 'Product Name'
              },
              {
                name: 'price',
                type: 'number',
                required: true,
                label: 'Price'
              },
              {
                name: 'description',
                type: 'richtext',
                required: false,
                label: 'Description'
              }
            ],
            taxonomy_ids: [1, 2]
          },
          responses: {
            201: {
              description: 'Content model created successfully',
              example: {
                status: 'success',
                message: 'Content model created successfully',
                data: {
                  id: 2
                }
              }
            }
          }
        }
      ]
    },
    {
      category: 'Content Entries',
      icon: FiFileText,
      color: 'teal',
      description: 'Manage multilingual content entries',
      endpoints: [
        {
          method: 'GET',
          path: '/api/content-entries/{tenant_id}',
          description: 'Get all content entries',
          summary: 'Retrieve content entries with filtering and pagination',
          parameters: [
            { name: 'tenant_id', type: 'path', required: true, description: 'Tenant ID' },
            { name: 'site_id', type: 'query', required: false, description: 'Filter by site' },
            { name: 'locale_id', type: 'query', required: false, description: 'Filter by locale' },
            { name: 'content_model_id', type: 'query', required: false, description: 'Filter by content model' },
            { name: 'status', type: 'query', required: false, description: 'Filter by status' },
            { name: 'page', type: 'query', required: false, description: 'Page number' },
            { name: 'limit', type: 'query', required: false, description: 'Items per page' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'Content entries retrieved successfully',
              example: {
                status: 'success',
                data: [
                  {
                    id: 1,
                    slug: 'my-blog-post',
                    content_model_id: 1,
                    site_id: 1,
                    created_at: '2025-01-07T10:30:00Z',
                    translations: [
                      {
                        id: 1,
                        locale_id: 1,
                        title: 'My Blog Post',
                        content: '<p>Content here</p>',
                        status: 'published'
                      }
                    ]
                  }
                ],
                pagination: {
                  page: 1,
                  limit: 20,
                  total: 1
                }
              }
            }
          }
        },
        {
          method: 'POST',
          path: '/api/content-entries',
          description: 'Create multilingual content entry',
          summary: 'Create a new content entry with multiple language translations',
          parameters: [],
          requestBody: {
            tenant_id: 1,
            site_id: 1,
            content_model_id: 1,
            slug: 'new-blog-post',
            translations: [
              {
                locale_id: 1,
                title: 'New Blog Post',
                content: '<p>English content</p>',
                excerpt: 'Short description',
                status: 'draft',
                custom_fields: {
                  featured_image: '/uploads/image.jpg',
                  author: 'John Doe'
                }
              },
              {
                locale_id: 2,
                title: 'Nueva Publicación',
                content: '<p>Contenido en español</p>',
                excerpt: 'Descripción corta',
                status: 'draft',
                custom_fields: {
                  featured_image: '/uploads/image.jpg',
                  author: 'John Doe'
                }
              }
            ]
          },
          responses: {
            201: {
              description: 'Content entry created successfully',
              example: {
                status: 'success',
                message: 'Content entry created successfully',
                data: {
                  id: 2,
                  slug: 'new-blog-post'
                }
              }
            }
          }
        }
      ]
    },
    {
      category: 'Taxonomies',
      icon: FiTag,
      color: 'pink',
      description: 'Manage multilingual taxonomies and terms',
      endpoints: [
        {
          method: 'GET',
          path: '/api/taxonomies/{tenant_id}',
          description: 'Get all taxonomies',
          summary: 'Retrieve all taxonomies for a tenant',
          parameters: [
            { name: 'tenant_id', type: 'path', required: true, description: 'Tenant ID' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'Taxonomies retrieved successfully',
              example: {
                success: true,
                data: [
                  {
                    id: 1,
                    name: 'Categories',
                    slug: 'categories',
                    created_at: '2025-01-07T10:30:00Z'
                  }
                ]
              }
            }
          }
        },
        {
          method: 'POST',
          path: '/api/taxonomies/{tenant_id}/{taxonomy_id}/terms',
          description: 'Create multilingual term',
          summary: 'Create a new term with translations',
          parameters: [
            { name: 'tenant_id', type: 'path', required: true, description: 'Tenant ID' },
            { name: 'taxonomy_id', type: 'path', required: true, description: 'Taxonomy ID' }
          ],
          requestBody: {
            slug: 'technology',
            translations: [
              {
                locale_id: 1,
                name: 'Technology',
                description: 'Tech related content'
              },
              {
                locale_id: 2,
                name: 'Tecnología',
                description: 'Contenido relacionado con tecnología'
              }
            ],
            parent_id: null,
            sort_order: 0
          },
          responses: {
            201: {
              description: 'Term created successfully',
              example: {
                id: 1,
                message: 'Term created successfully'
              }
            }
          }
        }
      ]
    },
    {
      category: 'Pages & Templates',
      icon: FiLayout,
      color: 'cyan',
      description: 'Create and manage pages with template system',
      endpoints: [
        {
          method: 'GET',
          path: '/api/pages/{tenant_id}',
          description: 'Get all pages',
          summary: 'Retrieve all pages for a tenant',
          parameters: [
            { name: 'tenant_id', type: 'path', required: true, description: 'Tenant ID' },
            { name: 'site_id', type: 'query', required: false, description: 'Filter by site' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'Pages retrieved successfully',
              example: {
                success: true,
                data: [
                  {
                    id: 1,
                    name: 'Home Page',
                    path: '/',
                    status: 'published',
                    site_id: 1,
                    template_id: 1,
                    is_home: true,
                    created_at: '2025-01-07T10:30:00Z'
                  }
                ]
              }
            }
          }
        },
        {
          method: 'POST',
          path: '/api/pages/{tenant_id}/{id}/versions',
          description: 'Create page version',
          summary: 'Create a new version of a page with layout data',
          parameters: [
            { name: 'tenant_id', type: 'path', required: true, description: 'Tenant ID' },
            { name: 'id', type: 'path', required: true, description: 'Page ID' }
          ],
          requestBody: {
            version_type: 'draft',
            layout_data: JSON.stringify([
              {
                type: 'hero',
                props: {
                  title: 'Welcome to our site',
                  subtitle: 'This is a hero section',
                  button_text: 'Get Started'
                }
              }
            ]),
            content: '<section class="hero">...</section>',
            full_html: '<!DOCTYPE html><html>...</html>',
            component_count: 1
          },
          responses: {
            201: {
              description: 'Page version created successfully',
              example: {
                success: true,
                data: {
                  id: 1,
                  page_id: 1,
                  version_type: 'draft'
                }
              }
            }
          }
        }
      ]
    },
    {
      category: 'Navigation',
      icon: FiNavigation,
      color: 'yellow',
      description: 'Manage site navigation menus',
      endpoints: [
        {
          method: 'GET',
          path: '/api/navigations/{tenant_id}',
          description: 'Get all navigations',
          summary: 'Retrieve all navigation menus for a tenant',
          parameters: [
            { name: 'tenant_id', type: 'path', required: true, description: 'Tenant ID' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'Navigations retrieved successfully',
              example: {
                success: true,
                data: [
                  {
                    id: 1,
                    name: 'Main Navigation',
                    site_id: 1,
                    created_at: '2025-01-07T10:30:00Z'
                  }
                ]
              }
            }
          }
        },
        {
          method: 'POST',
          path: '/api/navigations/{tenant_id}/items',
          description: 'Create navigation item',
          summary: 'Add a new item to a navigation menu',
          parameters: [
            { name: 'tenant_id', type: 'path', required: true, description: 'Tenant ID' }
          ],
          requestBody: {
            navigation_id: 1,
            label: 'Home',
            url: '/',
            type: 'internal',
            parent_id: null,
            sort_order: 0,
            is_active: true
          },
          responses: {
            201: {
              description: 'Navigation item created successfully',
              example: {
                success: true,
                message: 'Navigation item created successfully',
                data: {
                  id: 1
                }
              }
            }
          }
        }
      ]
    },
    {
      category: 'Assets',
      icon: FiImage,
      color: 'red',
      description: 'Manage media files and assets',
      endpoints: [
        {
          method: 'GET',
          path: '/api/assets/{tenant_id}',
          description: 'Get all assets',
          summary: 'Retrieve all assets for a tenant',
          parameters: [
            { name: 'tenant_id', type: 'path', required: true, description: 'Tenant ID' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'Assets retrieved successfully',
              example: {
                success: true,
                data: [
                  {
                    id: 1,
                    file_name: 'image.jpg',
                    file_path: '/uploads/image.jpg',
                    file_type: 'image/jpeg',
                    file_size: 1024000,
                    created_at: '2025-01-07T10:30:00Z'
                  }
                ]
              }
            }
          }
        },
        {
          method: 'POST',
          path: '/api/assets/upload',
          description: 'Upload asset file',
          summary: 'Upload a new file to the media library',
          parameters: [],
          requestBody: 'multipart/form-data with file field',
          responses: {
            201: {
              description: 'File uploaded successfully',
              example: {
                status: 'success',
                message: 'File uploaded successfully',
                data: {
                  id: 2,
                  file_name: 'newimage.jpg',
                  file_path: '/uploads/newimage.jpg'
                }
              }
            }
          }
        }
      ]
    },
    {
      category: 'A/B Testing',
      icon: FiTrendingUp,
      color: 'purple',
      description: 'Create and manage A/B tests with analytics',
      endpoints: [
        {
          method: 'GET',
          path: '/api/ab-tests/{tenant_id}',
          description: 'Get all A/B tests',
          summary: 'Retrieve all A/B tests for a tenant',
          parameters: [
            { name: 'tenant_id', type: 'path', required: true, description: 'Tenant ID' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'A/B tests retrieved successfully',
              example: {
                success: true,
                data: [
                  {
                    id: 1,
                    name: 'Button Color Test',
                    status: 'running',
                    entity_type: 'page_component',
                    traffic_split: 50,
                    created_at: '2025-01-07T10:30:00Z'
                  }
                ]
              }
            }
          }
        },
        {
          method: 'POST',
          path: '/api/ab-tests/{tenant_id}',
          description: 'Create A/B test',
          summary: 'Create a new A/B test for pages or components',
          parameters: [
            { name: 'tenant_id', type: 'path', required: true, description: 'Tenant ID' }
          ],
          requestBody: {
            name: 'Hero Button Color Test',
            description: 'Testing blue vs orange button colors',
            entity_type: 'page_component',
            entity_id: 1,
            variant_a_data: {
              button_color: '#0066cc',
              button_text: 'Get Started'
            },
            variant_b_data: {
              button_color: '#ff6600',
              button_text: 'Start Now'
            },
            traffic_split: 50,
            goal_metric: 'click_through_rate'
          },
          responses: {
            201: {
              description: 'A/B test created successfully',
              example: {
                success: true,
                message: 'A/B test created successfully',
                data: {
                  id: 1,
                  name: 'Hero Button Color Test',
                  status: 'draft'
                }
              }
            }
          }
        },
        {
          method: 'GET',
          path: '/api/ab-tests/{tenant_id}/{id}/results',
          description: 'Get test results',
          summary: 'Retrieve detailed analytics for an A/B test',
          parameters: [
            { name: 'tenant_id', type: 'path', required: true, description: 'Tenant ID' },
            { name: 'id', type: 'path', required: true, description: 'A/B Test ID' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'Test results retrieved successfully',
              example: {
                success: true,
                data: {
                  test_id: 1,
                  variant_a: {
                    impressions: 1000,
                    conversions: 85,
                    conversion_rate: 8.5
                  },
                  variant_b: {
                    impressions: 1000,
                    conversions: 92,
                    conversion_rate: 9.2
                  },
                  statistical_significance: 0.85,
                  winner: 'variant_b'
                }
              }
            }
          }
        }
      ]
    },
    {
      category: 'Scheduling',
      icon: FiCalendar,
      color: 'gray',
      description: 'Schedule content publication and management',
      endpoints: [
        {
          method: 'POST',
          path: '/api/schedule/{tenant_id}',
          description: 'Schedule publication',
          summary: 'Schedule content for automatic publication',
          parameters: [
            { name: 'tenant_id', type: 'path', required: true, description: 'Tenant ID' }
          ],
          requestBody: {
            entity_type: 'content_entry',
            entity_id: 1,
            scheduled_at: '2025-01-08T10:00:00Z',
            action: 'publish',
            version_id: 1
          },
          responses: {
            201: {
              description: 'Publication scheduled successfully',
              example: {
                success: true,
                message: 'Publication scheduled successfully',
                data: {
                  id: 1
                }
              }
            }
          }
        },
        {
          method: 'GET',
          path: '/api/schedule/{tenant_id}',
          description: 'Get scheduled publications',
          summary: 'Retrieve all scheduled publications',
          parameters: [
            { name: 'tenant_id', type: 'path', required: true, description: 'Tenant ID' },
            { name: 'entity_type', type: 'query', required: false, description: 'Filter by entity type' },
            { name: 'status', type: 'query', required: false, description: 'Filter by status' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'Scheduled publications retrieved',
              example: {
                success: true,
                data: [
                  {
                    id: 1,
                    entity_type: 'content_entry',
                    entity_id: 1,
                    scheduled_at: '2025-01-08T10:00:00Z',
                    status: 'pending',
                    action: 'publish'
                  }
                ]
              }
            }
          }
        }
      ]
    },
    {
      category: 'API Keys',
      icon: FiKey,
      color: 'indigo',
      description: 'Manage API keys for external access',
      endpoints: [
        {
          method: 'GET',
          path: '/api/api-keys/{tenant_id}',
          description: 'Get all API keys',
          summary: 'Retrieve all API keys for a tenant',
          parameters: [
            { name: 'tenant_id', type: 'path', required: true, description: 'Tenant ID' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'API keys retrieved successfully',
              example: {
                status: 'success',
                data: [
                  {
                    id: 1,
                    name: 'Production API',
                    api_key: 'ak_live_12345...',
                    mode: 'live',
                    rate_limit: 1000,
                    permissions: ['read', 'write'],
                    created_at: '2025-01-07T10:30:00Z'
                  }
                ]
              }
            }
          }
        },
        {
          method: 'POST',
          path: '/api/api-keys',
          description: 'Create API key',
          summary: 'Generate a new API key for external access',
          parameters: [],
          requestBody: {
            tenant_id: 1,
            name: 'Mobile App API',
            rate_limit: 500,
            permissions: ['read'],
            mode: 'test'
          },
          responses: {
            201: {
              description: 'API key created successfully',
              example: {
                status: 'success',
                message: 'API key created successfully',
                data: {
                  id: 2,
                  api_key: 'ak_test_67890...',
                  secret_key: 'sk_test_abcdef...'
                }
              }
            }
          }
        }
      ]
    },
    {
      category: 'Locales',
      icon: FiGlobe,
      color: 'blue',
      description: 'Manage languages and localization',
      endpoints: [
        {
          method: 'GET',
          path: '/api/locales/{site_id}',
          description: 'Get locales by site',
          summary: 'Retrieve all locales available for a site',
          parameters: [
            { name: 'site_id', type: 'path', required: true, description: 'Site ID' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'Locales retrieved successfully',
              example: {
                success: true,
                data: [
                  {
                    id: 1,
                    code: 'en',
                    name: 'English',
                    native_name: 'English',
                    is_default: true,
                    is_active: true
                  },
                  {
                    id: 2,
                    code: 'es',
                    name: 'Spanish',
                    native_name: 'Español',
                    is_default: false,
                    is_active: true
                  }
                ]
              }
            }
          }
        },
        {
          method: 'POST',
          path: '/api/locales',
          description: 'Create locale',
          summary: 'Create a new locale for sites',
          parameters: [],
          requestBody: {
            tenant_id: 1,
            code: 'fr',
            name: 'French',
            native_name: 'Français',
            site_ids: [1, 2]
          },
          responses: {
            201: {
              description: 'Locale created successfully',
              example: {
                status: 'success',
                message: 'Locale created successfully',
                data: {
                  id: 3
                }
              }
            }
          }
        }
      ]
    }
  ];

  // Delivery API endpoints (public-facing)
  const deliveryApiEndpoints = [
    {
      category: 'Content Delivery',
      icon: FiServer,
      color: 'teal',
      description: 'Public API for content delivery',
      endpoints: [
        {
          method: 'GET',
          path: '/v1/content',
          description: 'Get published content',
          summary: 'Retrieve published content entries with filtering',
          parameters: [
            { name: 'tenant_id', type: 'query', required: true, description: 'Tenant ID' },
            { name: 'site_id', type: 'query', required: false, description: 'Filter by site' },
            { name: 'locale_id', type: 'query', required: false, description: 'Filter by locale' },
            { name: 'content_model_id', type: 'query', required: false, description: 'Filter by content model' },
            { name: 'limit', type: 'query', required: false, description: 'Number of items to return' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'Content retrieved successfully',
              example: {
                status: 'success',
                data: [
                  {
                    id: 1,
                    title: 'Blog Post Title',
                    content: '<p>Published content</p>',
                    slug: 'blog-post-title',
                    published_at: '2025-01-07T10:30:00Z',
                    custom_fields: {
                      featured_image: '/uploads/image.jpg',
                      author: 'John Doe'
                    }
                  }
                ]
              }
            }
          }
        },
        {
          method: 'GET',
          path: '/v1/content/{id}',
          description: 'Get single content entry',
          summary: 'Retrieve a specific published content entry',
          parameters: [
            { name: 'id', type: 'path', required: true, description: 'Content ID' },
            { name: 'tenant_id', type: 'query', required: true, description: 'Tenant ID' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'Content entry retrieved successfully',
              example: {
                status: 'success',
                data: {
                  id: 1,
                  title: 'Blog Post Title',
                  content: '<p>Full content here</p>',
                  slug: 'blog-post-title',
                  published_at: '2025-01-07T10:30:00Z',
                  translations: [
                    {
                      locale_id: 1,
                      title: 'Blog Post Title',
                      content: '<p>English content</p>'
                    },
                    {
                      locale_id: 2,
                      title: 'Título del Post',
                      content: '<p>Contenido en español</p>'
                    }
                  ]
                }
              }
            }
          }
        }
      ]
    },
    {
      category: 'Taxonomy Delivery',
      icon: FiTag,
      color: 'pink',
      description: 'Public taxonomy and terms API',
      endpoints: [
        {
          method: 'GET',
          path: '/v1/taxonomies',
          description: 'Get all taxonomies',
          summary: 'Retrieve all available taxonomies',
          parameters: [
            { name: 'tenant_id', type: 'query', required: true, description: 'Tenant ID' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'Taxonomies retrieved successfully',
              example: {
                status: 'success',
                data: [
                  {
                    id: 1,
                    name: 'Categories',
                    slug: 'categories',
                    terms_count: 5
                  }
                ]
              }
            }
          }
        },
        {
          method: 'GET',
          path: '/v1/taxonomies/{id}/terms',
          description: 'Get taxonomy terms',
          summary: 'Retrieve all terms for a specific taxonomy',
          parameters: [
            { name: 'id', type: 'path', required: true, description: 'Taxonomy ID' },
            { name: 'tenant_id', type: 'query', required: true, description: 'Tenant ID' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'Terms retrieved successfully',
              example: {
                status: 'success',
                data: [
                  {
                    id: 1,
                    name: 'Technology',
                    slug: 'technology',
                    description: 'Tech related content',
                    content_count: 12
                  }
                ]
              }
            }
          }
        }
      ]
    },
    {
      category: 'Site Information',
      icon: FiGlobe,
      color: 'green',
      description: 'Public site configuration API',
      endpoints: [
        {
          method: 'GET',
          path: '/v1/sites',
          description: 'Get site information',
          summary: 'Retrieve public site configuration',
          parameters: [
            { name: 'tenant_id', type: 'query', required: true, description: 'Tenant ID' }
          ],
          requestBody: null,
          responses: {
            200: {
              description: 'Site information retrieved',
              example: {
                status: 'success',
                data: [
                  {
                    id: 1,
                    name: 'Main Website',
                    domain: 'example.com',
                    default_locale: {
                      code: 'en',
                      name: 'English'
                    },
                    available_locales: [
                      {
                        code: 'en',
                        name: 'English'
                      },
                      {
                        code: 'es',
                        name: 'Spanish'
                      }
                    ]
                  }
                ]
              }
            }
          }
        }
      ]
    }
  ];

  const getMethodColor = (method) => {
    switch (method) {
      case 'GET': return 'green';
      case 'POST': return 'blue';
      case 'PUT': return 'orange';
      case 'DELETE': return 'red';
      default: return 'gray';
    }
  };

  const simulateApiCall = async (endpoint) => {
    setIsLoading(true);
    setApiResponse(null);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Return mock response based on first successful response
    const mockResponse = Object.values(endpoint.responses)[0];
    setApiResponse({
      status: Object.keys(endpoint.responses)[0],
      data: mockResponse.example
    });
    setIsLoading(false);
  };

  const EndpointDetails = ({ endpoint }) => (
    <MotionBox
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <HStack spacing={3} mb={2}>
            <Badge colorScheme={getMethodColor(endpoint.method)} fontSize="sm" px={3} py={1}>
              {endpoint.method}
            </Badge>
            <Code fontSize="lg" p={2} bg={codeBgColor} borderRadius="md">
              {endpoint.path}
            </Code>
          </HStack>
          <Text fontSize="lg" fontWeight="semibold" mb={2}>
            {endpoint.description}
          </Text>
          <Text color={textColor}>
            {endpoint.summary}
          </Text>
        </Box>

        {/* Parameters */}
        {endpoint.parameters && endpoint.parameters.length > 0 && (
          <Box>
            <Heading size="sm" mb={3}>Parameters</Heading>
            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Type</Th>
                    <Th>Required</Th>
                    <Th>Description</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {endpoint.parameters.map((param, idx) => (
                    <Tr key={idx}>
                      <Td><Code fontSize="sm">{param.name}</Code></Td>
                      <Td><Badge variant="outline">{param.type}</Badge></Td>
                      <Td>{param.required ? <Badge colorScheme="red">Required</Badge> : <Badge>Optional</Badge>}</Td>
                      <Td>{param.description}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Request Body */}
        {endpoint.requestBody && (
          <Box>
            <Heading size="sm" mb={3}>Request Body</Heading>
            <Card variant="outline">
              <CardBody>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" color={textColor}>JSON</Text>
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<FiCopy />}
                    onClick={() => copyToClipboard(
                      typeof endpoint.requestBody === 'string' 
                        ? endpoint.requestBody 
                        : JSON.stringify(endpoint.requestBody, null, 2)
                    )}
                  >
                    Copy
                  </Button>
                </HStack>
                <Code
                  display="block"
                  whiteSpace="pre-wrap"
                  p={4}
                  bg={codeBgColor}
                  borderRadius="md"
                  fontSize="sm"
                  overflow="auto"
                >
                  {typeof endpoint.requestBody === 'string' 
                    ? endpoint.requestBody 
                    : JSON.stringify(endpoint.requestBody, null, 2)
                  }
                </Code>
              </CardBody>
            </Card>
          </Box>
        )}

        {/* Try It Out */}
        <Box>
          <Heading size="sm" mb={3}>Try it out</Heading>
          <Card variant="outline">
            <CardBody>
              <VStack spacing={3} align="stretch">
                <Button
                  leftIcon={<FiPlay />}
                  colorScheme="blue"
                  onClick={() => simulateApiCall(endpoint)}
                  isLoading={isLoading}
                  loadingText="Sending request..."
                >
                  Send Request
                </Button>
                
                {apiResponse && (
                  <Box>
                    <HStack justify="space-between" mb={2}>
                      <HStack>
                        <Text fontSize="sm" fontWeight="semibold">Response</Text>
                        <Badge colorScheme={apiResponse.status.startsWith('2') ? 'green' : 'red'}>
                          {apiResponse.status}
                        </Badge>
                      </HStack>
                      <Button
                        size="sm"
                        variant="ghost"
                        leftIcon={<FiCopy />}
                        onClick={() => copyToClipboard(JSON.stringify(apiResponse.data, null, 2))}
                      >
                        Copy
                      </Button>
                    </HStack>
                    <Code
                      display="block"
                      whiteSpace="pre-wrap"
                      p={4}
                      bg={codeBgColor}
                      borderRadius="md"
                      fontSize="sm"
                      overflow="auto"
                    >
                      {JSON.stringify(apiResponse.data, null, 2)}
                    </Code>
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>
        </Box>

        {/* Responses */}
        <Box>
          <Heading size="sm" mb={3}>Responses</Heading>
          <VStack spacing={3} align="stretch">
            {Object.entries(endpoint.responses).map(([status, response]) => (
              <Card key={status} variant="outline">
                <CardHeader py={3}>
                  <HStack justify="space-between">
                    <HStack>
                      <Badge colorScheme={status.startsWith('2') ? 'green' : 'red'}>
                        {status}
                      </Badge>
                      <Text fontWeight="semibold">{response.description}</Text>
                    </HStack>
                    <Button
                      size="sm"
                      variant="ghost"
                      leftIcon={<FiCopy />}
                      onClick={() => copyToClipboard(JSON.stringify(response.example, null, 2))}
                    >
                      Copy
                    </Button>
                  </HStack>
                </CardHeader>
                <CardBody pt={0}>
                  <Code
                    display="block"
                    whiteSpace="pre-wrap"
                    p={4}
                    bg={codeBgColor}
                    borderRadius="md"
                    fontSize="sm"
                    overflow="auto"
                  >
                    {JSON.stringify(response.example, null, 2)}
                  </Code>
                </CardBody>
              </Card>
            ))}
          </VStack>
        </Box>
      </VStack>
    </MotionBox>
  );

  const ApiSidebar = ({ endpoints }) => (
    <Box bg={sidebarBg} p={4} borderRadius="md" h="fit-content" position="sticky" top={4}>
      <VStack spacing={3} align="stretch">
        <Text fontWeight="bold" fontSize="lg">API Reference</Text>
        <Divider />
        
        {endpoints.map((category) => (
          <Box key={category.category}>
            <Button
              variant="ghost"
              size="sm"
              justifyContent="flex-start"
              width="full"
              leftIcon={<Icon as={expandedCategories[category.category] ? FiChevronDown : FiChevronRight} />}
              onClick={() => toggleCategory(category.category)}
              fontWeight="semibold"
            >
              <HStack spacing={2} flex={1}>
                <Icon as={category.icon} color={`${category.color}.500`} />
                <Text>{category.category}</Text>
              </HStack>
            </Button>
            
            <Collapse in={expandedCategories[category.category]}>
              <VStack spacing={1} align="stretch" ml={6} mt={2}>
                {category.endpoints.map((endpoint, idx) => (
                  <Button
                    key={idx}
                    variant="ghost"
                    size="sm"
                    justifyContent="flex-start"
                    width="full"
                    leftIcon={
                      <Badge size="sm" colorScheme={getMethodColor(endpoint.method)} fontSize="xs">
                        {endpoint.method}
                      </Badge>
                    }
                    onClick={() => setSelectedEndpoint(endpoint)}
                    bg={selectedEndpoint === endpoint ? hoverBg : 'transparent'}
                    _hover={{ bg: hoverBg }}
                  >
                    <Text fontSize="sm" isTruncated flex={1} textAlign="left">
                      {endpoint.description}
                    </Text>
                  </Button>
                ))}
              </VStack>
            </Collapse>
          </Box>
        ))}
      </VStack>
    </Box>
  );

  const currentEndpoints = activeTab === 0 ? managementApiEndpoints : deliveryApiEndpoints;

  return (
    <Container maxW="container.xl" py={8}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Documentation</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Box textAlign="center">
          <Heading size="xl" mb={4}>CMS Dashboard API Documentation</Heading>
          <Text fontSize="lg" color={textColor} maxW="2xl" mx="auto">
            Comprehensive API documentation for content management and delivery. Test endpoints interactively.
          </Text>
        </Box>

        {/* API Type Tabs */}
        <Tabs index={activeTab} onChange={setActiveTab} variant="enclosed">
          <TabList>
            <Tab>
              <HStack>
                <Icon as={FiShield} />
                <Text>Management API</Text>
              </HStack>
            </Tab>
            <Tab>
              <HStack>
                <Icon as={FiServer} />
                <Text>Delivery API</Text>
              </HStack>
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel p={0} pt={6}>
              {/* Management API Alert */}
              <Alert status="info" mb={6}>
                <AlertIcon />
                <VStack align="start" spacing={1}>
                  <Text fontWeight="semibold">Management API</Text>
                  <Text fontSize="sm">Requires JWT authentication. Base URL: https://your-domain.com/cms-backend/api</Text>
                </VStack>
              </Alert>
            </TabPanel>
            
            <TabPanel p={0} pt={6}>
              {/* Delivery API Alert */}
              <Alert status="success" mb={6}>
                <AlertIcon />
                <VStack align="start" spacing={1}>
                  <Text fontWeight="semibold">Delivery API</Text>
                  <Text fontSize="sm">Requires API key authentication. Base URL: https://your-domain.com/cms-backend/delivery</Text>
                </VStack>
              </Alert>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Main Content */}
        <Flex gap={6} align="flex-start">
          {/* Sidebar */}
          <Box w="300px" flexShrink={0}>
            <ApiSidebar endpoints={currentEndpoints} />
          </Box>

          {/* Main Content Area */}
          <Box flex={1} minH="80vh">
            {selectedEndpoint ? (
              <EndpointDetails endpoint={selectedEndpoint} />
            ) : (
              <Card bg={cardBg} borderColor={borderColor} variant="outline">
                <CardBody textAlign="center" py={12}>
                  <VStack spacing={4}>
                    <Icon as={FiEye} boxSize={12} color="gray.400" />
                    <VStack spacing={2}>
                      <Heading size="md" color={textColor}>Select an Endpoint</Heading>
                      <Text color={textColor}>
                        Choose an API endpoint from the sidebar to view detailed documentation and try it out.
                      </Text>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            )}
          </Box>
        </Flex>

        {/* Quick Start Guide */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardHeader>
            <Heading size="md">Quick Start Guide</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Heading size="sm" mb={2}>1. Authentication</Heading>
                <Text fontSize="sm" color={textColor} mb={2}>
                  {activeTab === 0 
                    ? 'Management API uses JWT tokens in the Authorization header:'
                    : 'Delivery API uses API keys in headers or query parameters:'
                  }
                </Text>
                <Code display="block" p={3} bg={codeBgColor} borderRadius="md">
                  {activeTab === 0 
                    ? 'Authorization: Bearer your_jwt_token_here'
                    : 'X-Api-Key: your_api_key\nX-Secret-Key: your_secret_key'
                  }
                </Code>
              </Box>

              <Box>
                <Heading size="sm" mb={2}>2. Content-Type</Heading>
                <Text fontSize="sm" color={textColor} mb={2}>
                  All POST and PUT requests should include the Content-Type header:
                </Text>
                <Code display="block" p={3} bg={codeBgColor} borderRadius="md">
                  Content-Type: application/json
                </Code>
              </Box>

              <Box>
                <Heading size="sm" mb={2}>3. Response Format</Heading>
                <Text fontSize="sm" color={textColor} mb={2}>
                  All responses follow a consistent format:
                </Text>
                <Code display="block" p={3} bg={codeBgColor} borderRadius="md" whiteSpace="pre-wrap">
{`{
  "success": true,
  "data": { /* your data here */ },
  "message": "Optional success message"
}

// Or for errors:
{
  "success": false,
  "error": "Error message here"
}`}
                </Code>
              </Box>

              {activeTab === 1 && (
                <Box>
                  <Heading size="sm" mb={2}>4. Rate Limiting</Heading>
                  <Text fontSize="sm" color={textColor} mb={2}>
                    Delivery API requests are rate-limited based on your API key configuration:
                  </Text>
                  <Code display="block" p={3} bg={codeBgColor} borderRadius="md">
                    X-RateLimit-Limit: 1000{'\n'}X-RateLimit-Remaining: 999{'\n'}X-RateLimit-Reset: 1641024000
                  </Code>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Entity Overview */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardHeader>
            <Heading size="md">Entity Overview</Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              {[
                { name: 'Users', icon: FiUser, color: 'blue' },
                { name: 'Tenants', icon: FiUsers, color: 'purple' },
                { name: 'Sites', icon: FiGlobe, color: 'green' },
                { name: 'Content Models', icon: FiDatabase, color: 'orange' },
                { name: 'Content Entries', icon: FiFileText, color: 'teal' },
                { name: 'Taxonomies', icon: FiTag, color: 'pink' },
                { name: 'Pages', icon: FiLayout, color: 'cyan' },
                { name: 'Navigation', icon: FiNavigation, color: 'yellow' },
                { name: 'Assets', icon: FiImage, color: 'red' },
                { name: 'Templates', icon: FiGrid, color: 'gray' },
                { name: 'A/B Tests', icon: FiTrendingUp, color: 'purple' },
                { name: 'Scheduling', icon: FiCalendar, color: 'indigo' },
                { name: 'API Keys', icon: FiKey, color: 'blue' },
                { name: 'Locales', icon: FiMap, color: 'green' },
                { name: 'Components', icon: FiFolder, color: 'orange' },
                { name: 'Subscriptions', icon: FiCreditCard, color: 'teal' }
              ].map((entity) => (
                <Card key={entity.name} variant="outline" size="sm">
                  <CardBody textAlign="center">
                    <Icon as={entity.icon} boxSize={6} color={`${entity.color}.500`} mb={2} />
                    <Text fontSize="sm" fontWeight="medium">{entity.name}</Text>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
};

export default Documentation;